import React, { useMemo } from 'react';

/**
 * Unified diff, rendered like a code view rather than a coloured list.
 *
 * Takes the row list from `diffLines(draftToLines(a), draftToLines(b))`, which
 * already flattens EVERYTHING a version holds — messages, model config, tools,
 * output format and declared variables — so a temperature change shows up here
 * the same way a reworded sentence does.
 *
 * Two gutters, old line number then new, which is what makes a unified diff
 * readable: a deletion numbers on the left only, an addition on the right only,
 * and unchanged rows carry both. Numbers are computed here rather than in the
 * differ because they're a property of how the diff is DISPLAYED — the same row
 * list rendered side-by-side would number differently.
 *
 * Shared by the save modal (loaded version → draft) and the version compare, so
 * the two can't drift apart.
 */

const ROW = {
  add: {
    row: 'bg-[color:var(--feedback-success)]/[0.09]',
    text: 'text-[color:var(--text-primary)]',
    sign: 'text-[color:var(--feedback-success)]',
    glyph: '+',
  },
  del: {
    row: 'bg-[color:var(--feedback-error)]/[0.09]',
    text: 'text-[color:var(--text-primary)]',
    sign: 'text-[color:var(--feedback-error)]',
    glyph: '−',
  },
  ctx: {
    row: '',
    text: 'text-[color:var(--text-secondary)]',
    sign: 'text-transparent',
    glyph: ' ',
  },
};

/** Section headers the line builder emits, e.g. `# model config`, `@system`. */
const isHeading = (t) => /^[#@]/.test(t || '');

export default function DiffView({ diff, emptyLabel = 'No content.', maxHeight = 280 }) {
  // Walk once, assigning each side's running line number.
  const rows = useMemo(() => {
    let oldNo = 0;
    let newNo = 0;
    return (diff || []).map((r) => {
      if (r.type === 'add') { newNo += 1; return { ...r, oldNo: null, newNo }; }
      if (r.type === 'del') { oldNo += 1; return { ...r, oldNo, newNo: null }; }
      oldNo += 1; newNo += 1;
      return { ...r, oldNo, newNo };
    });
  }, [diff]);

  const stats = useMemo(() => ({
    add: rows.filter((r) => r.type === 'add').length,
    del: rows.filter((r) => r.type === 'del').length,
  }), [rows]);

  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)]">
      {rows.length > 0 && (
        <div className="flex items-center gap-2 border-b border-[color:var(--border-subtle)] px-3 py-1.5">
          <span className="font-mono text-[10.5px] tabular-nums text-[color:var(--feedback-success)]">+{stats.add}</span>
          <span className="font-mono text-[10.5px] tabular-nums text-[color:var(--feedback-error)]">−{stats.del}</span>
        </div>
      )}
      <div
        className="overflow-auto font-mono text-[11px] leading-[1.6]"
        style={{ maxHeight }}
      >
        {rows.length === 0 ? (
          <div className="px-3 py-2 text-[11.5px] text-[color:var(--text-muted)]">{emptyLabel}</div>
        ) : (
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, i) => {
                const s = ROW[row.type] || ROW.ctx;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i} className={s.row}>
                    <td className="w-9 select-none border-r border-[color:var(--border-subtle)] px-1.5 text-right align-top text-[10px] tabular-nums text-[color:var(--text-disabled)]">
                      {row.oldNo ?? ''}
                    </td>
                    <td className="w-9 select-none border-r border-[color:var(--border-subtle)] px-1.5 text-right align-top text-[10px] tabular-nums text-[color:var(--text-disabled)]">
                      {row.newNo ?? ''}
                    </td>
                    <td className={`w-4 select-none pl-1.5 text-center align-top ${s.sign}`}>{s.glyph}</td>
                    <td
                      className={`whitespace-pre-wrap break-words py-px pr-3 align-top ${s.text} ${
                        isHeading(row.text) ? 'font-semibold text-[color:var(--text-primary)]' : ''
                      }`}
                    >
                      {row.text || ' '}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
